import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { tr } from 'helper/translate';
import { DragDropContext } from 'react-beautiful-dnd';
import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { EOfferVersionContentBlockLineItemSource, EOfferVersionContentBlockType } from '__generated__/graphql';
import {
  calculatePdfImportAmounts,
  PdfImportContext,
  PdfImportTaxShare,
  PdfOfferImportReview,
  pdfImportHasMixedTax,
  pdfImportSplitShares,
} from 'semshared/offer/pdf-import';

const percent = (rate: number): string => (Math.round(rate * 10000) / 100).toLocaleString('de-AT');
import { NO_PRICE } from 'semshared/pricelist/quickprice';
import { EditorAccordion } from './template_editor/EditorAccordion';
import { ContentBlockFormType, OfferVersionFormType } from './template_editor/context';

interface Props {
  review: PdfOfferImportReview;
  context: PdfImportContext | null;
  disabled: boolean;
  onChange: (review: PdfOfferImportReview) => void;
  onTaxChange: (index: number, taxRateId: number | null) => void;
  /** Applies a mixed-VAT split to the given lines. */
  onSplitChange: (indexes: number[], shares: PdfImportTaxShare[]) => void;
  /** Mixed VAT chosen manually when the catalog has no matching service. */
  onMixedChange: (index: number, taxRateIds: number[]) => void;
}

const shareKey = (shares: PdfImportTaxShare[]): string => shares.map(share => share.taxRateId).join(',');

/** Review the unsaved extraction with the same day/line editor as a persisted offer. */
export function PdfImportEditor({ review, context, disabled, onChange, onTaxChange, onSplitChange, onMixedChange }: Props) {
  const [editingTaxes, setEditingTaxes] = useState<number[]>([]);
  const [splitInputs, setSplitInputs] = useState<Record<number, string[]>>({});
  const splitForm = (lineIndex: number) => {
    const line = review.lineItems[lineIndex];
    const basis = tr(line.priceBasis === 'NET' ? 'pdf-import-basis-net' : 'pdf-import-basis-gross');
    const total = line.priceBasis === 'NET' ? line.totalNet : line.totalGross;
    const unit = total !== null && line.quantity ? total / line.quantity : null;
    const values =
      splitInputs[lineIndex] ||
      line.taxShares.slice(0, -1).map(share => (share.share !== null && unit !== null ? String(Math.round(unit * share.share * 100) / 100) : ''));
    const shares = pdfImportSplitShares(
      line,
      values.map(value => (value === '' ? null : Number(value))),
    );
    const same = review.lineItems.flatMap((other, index) =>
      other.description === line.description && shareKey(other.taxShares) === shareKey(line.taxShares) ? [index] : [],
    );
    const apply = (indexes: number[]) => {
      if (!shares) return;
      onSplitChange(indexes, shares);
      setSplitInputs(current => Object.fromEntries(Object.entries(current).filter(([key]) => !indexes.includes(Number(key)))));
      setEditingTaxes(current => current.filter(index => !indexes.includes(index)));
    };
    return (
      <Stack key={lineIndex} spacing={1} sx={{ p: 1.5, border: 1, borderColor: shares ? 'divider' : 'warning.light', borderRadius: 1 }}>
        <Typography variant="body2">
          {tr('pdf-import-split-intro', { item: line.description || tr('pdf-import-item', { n: lineIndex + 1 }), basis })}
          {unit !== null ? tr('pdf-import-split-unit', { price: unit.toLocaleString('de-AT', { minimumFractionDigits: 2 }) }) : ''}.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          {line.taxShares.slice(0, -1).map((share, index) => (
            <TextField
              key={share.taxRateId}
              size="small"
              type="number"
              inputProps={{ step: '0.01', min: 0 }}
              disabled={disabled || unit === null}
              label={tr('pdf-import-split-share', { rate: percent(share.rate!) })}
              value={values[index]}
              onChange={event =>
                setSplitInputs(current => ({ ...current, [lineIndex]: values.map((value, i) => (i === index ? event.target.value : value)) }))
              }
            />
          ))}
          <Typography variant="body2" color="text.secondary">
            {tr('pdf-import-split-rest', { rate: percent(line.taxShares[line.taxShares.length - 1].rate!) })}{' '}
            {unit !== null && shares
              ? `${(unit * shares[shares.length - 1].share!).toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
              : '–'}
          </Typography>
        </Stack>
        {unit === null && <Typography variant="caption">{tr('pdf-import-split-check-first')}</Typography>}
        {unit !== null && !shares && values.some(Boolean) && (
          <Typography variant="caption" color="error">
            {tr('pdf-import-split-too-high')}
          </Typography>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" disabled={disabled || !shares} onClick={() => apply([lineIndex])}>
            {tr('pdf-import-apply')}
          </Button>
          {same.length > 1 && (
            <Button size="small" disabled={disabled || !shares} onClick={() => apply(same)}>
              {tr('pdf-import-split-apply-all', { count: same.length, item: line.description.slice(0, 40) })}
            </Button>
          )}
          <Button size="small" disabled={disabled} onClick={() => onTaxChange(lineIndex, null)}>
            {tr('pdf-import-split-single-instead')}
          </Button>
        </Stack>
      </Stack>
    );
  };
  const groups = useMemo(() => {
    const result = new Map<string, number[]>();
    review.lineItems.forEach((line, index) => {
      const key = `${line.date}:${line.priceBasis}`;
      result.set(key, [...(result.get(key) || []), index]);
    });
    return [...result.values()].sort((a, b) => review.lineItems[a[0]].date.localeCompare(review.lineItems[b[0]].date));
  }, [review.lineItems]);
  const blocks = useMemo<ContentBlockFormType[]>(
    () =>
      groups.map((indexes, sequence) => {
        const first = review.lineItems[indexes[0]];
        const day = Math.round((Date.parse(first.date) - Date.parse(review.event.startDate)) / 86400000) || 0;
        return {
          type: EOfferVersionContentBlockType.LINE_ITEMS,
          sequence,
          day,
          blockDescription: tr(first.priceBasis === 'NET' ? 'pdf-import-net-prices' : 'pdf-import-gross-prices'),
          template: '',
          formFields: [],
          lineItems: indexes.map(index => {
            const line = review.lineItems[index];
            const tax = context?.taxRates.find(rate => rate.id === line.taxRateId);
            return {
              id: index + 1,
              day,
              sequence: index,
              source: EOfferVersionContentBlockLineItemSource.MANUAL,
              sku: null,
              header: line.description,
              details: line.notes,
              count: line.quantity ?? 0,
              priceItem: line.unitPrice ?? NO_PRICE,
              priceNet: line.totalNet ?? NO_PRICE,
              priceGross: line.totalGross ?? NO_PRICE,
              components: pdfImportHasMixedTax(line)
                ? line.taxShares.map(share => ({
                    taxTypeId: share.taxTypeId!,
                    name: `${percent(share.rate!)} % (${Math.round(share.share! * 100)} %)`,
                    price: line.unitPrice === null ? NO_PRICE : line.unitPrice * share.share!,
                  }))
                : [
                    {
                      taxTypeId: tax?.taxTypeId || 0,
                      name: tax ? `${tax.name} (${(tax.rate * 100).toFixed(2)} %)` : tr('pdf-import-tax-open'),
                      price: line.unitPrice ?? NO_PRICE,
                    },
                  ],
              taxes: pdfImportHasMixedTax(line)
                ? line.taxShares.map(share => ({
                    taxTypeId: share.taxTypeId!,
                    taxRateId: share.taxRateId!,
                    name: `${percent(share.rate!)} %`,
                    rate: share.rate!,
                    price: NO_PRICE,
                  }))
                : tax
                  ? [{ taxTypeId: tax.taxTypeId, taxRateId: tax.id, name: tax.name, rate: tax.rate, price: line.taxAmount ?? NO_PRICE }]
                  : [],
            };
          }),
        };
      }),
    [review, context, groups],
  );
  const form = useForm<OfferVersionFormType>({ defaultValues: { startDate: review.event.startDate, contentBlocks: blocks } });
  const state = useRef({ review, groups, onChange, disabled });
  state.current = { review, groups, onChange, disabled };
  const emitted = useRef('');
  const synchronizing = useRef(false);
  useEffect(() => {
    if (JSON.stringify(review) === emitted.current) return;
    synchronizing.current = true;
    form.reset({ startDate: review.event.startDate, contentBlocks: blocks });
    synchronizing.current = false;
  }, [review, blocks, form.reset]);
  const publish = useCallback(() => {
    if (synchronizing.current || state.current.disabled) return;
    const source = state.current.review;
    const next = structuredClone(source);
    let changed = false;
    let changedPrices = false;
    form.getValues('contentBlocks').forEach((block, blockIndex) =>
      block.lineItems?.forEach((item, itemIndex) => {
        const index = state.current.groups[blockIndex]?.[itemIndex];
        if (index === undefined) return;
        const line = next.lineItems[index];
        const unitPrice = item.priceItem === NO_PRICE ? null : item.priceItem;
        const changedPrice = unitPrice !== line.unitPrice || item.count !== (line.quantity ?? 0);
        if (changedPrice) {
          line.unitPrice = unitPrice;
          line.quantity = item.count;
          line.totalNet = line.totalGross = line.taxAmount = null;
          changedPrices = true;
        }
        if (changedPrice || item.header !== line.description || (item.details || '') !== line.notes) changed = true;
        line.description = item.header;
        line.notes = item.details || '';
      }),
    );
    if (!changed) return;
    if (changedPrices) next.totalNet = next.totalGross = next.totalTax = null;
    const result = calculatePdfImportAmounts(next).review;
    synchronizing.current = true;
    state.current.groups.forEach((indexes, blockIndex) =>
      indexes.forEach((lineIndex, itemIndex) => {
        const line = result.lineItems[lineIndex];
        form.setValue(`contentBlocks.${blockIndex}.lineItems.${itemIndex}.priceNet`, line.totalNet ?? NO_PRICE);
        form.setValue(`contentBlocks.${blockIndex}.lineItems.${itemIndex}.priceGross`, line.totalGross ?? NO_PRICE);
      }),
    );
    synchronizing.current = false;
    emitted.current = JSON.stringify(result);
    state.current.onChange(result);
  }, [form.getValues, form.setValue]);
  const copyPrices = useCallback((blockIndex: number, itemIndex: number) => {
    if (state.current.disabled) return;
    const index = state.current.groups[blockIndex]?.[itemIndex];
    const source = state.current.review;
    const selected = source.lineItems[index];
    if (!selected) return;
    const result = calculatePdfImportAmounts({
      ...source,
      totalNet: null,
      totalGross: null,
      totalTax: null,
      lineItems: source.lineItems.map((line, lineIndex) =>
        lineIndex !== index && line.description === selected.description && line.priceBasis === selected.priceBasis
          ? { ...line, unitPrice: selected.unitPrice, totalNet: null, totalGross: null, taxAmount: null }
          : line,
      ),
    }).review;
    emitted.current = '';
    state.current.onChange(result);
  }, []);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const subscription = form.watch(() => {
      if (synchronizing.current) return;
      clearTimeout(timer);
      timer = setTimeout(publish, 150);
    });
    return () => {
      clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, [form.watch, publish]);
  return (
    <FormProvider {...form}>
      <DragDropContext onDragEnd={() => undefined}>
        <Stack spacing={2} onBlurCapture={publish}>
          {blocks.map((block, index) => (
            <React.Fragment key={`${review.lineItems[groups[index][0]].date}:${review.lineItems[groups[index][0]].priceBasis}`}>
              <EditorAccordion
                cssStyles=""
                contentBlock={block}
                contentBlockIndex={index}
                showHtmlTab={false}
                fullWidth
                control={form.control}
                disabled={disabled}
                canChangeCounts
                canChangePrices
                canChangeItems
                canChangeStructure={false}
                canDeleteItems={false}
                canAddItems={false}
                isPricesNet={review.lineItems[groups[index][0]].priceBasis === 'NET'}
                priceListId={review.priceListId || undefined}
                onLineItemChanged={publish}
                onLineItemCopyPrices={copyPrices}
                onLineItemPriceComponentChanged={publish}
                onLineItemSKUAdded={() => null}
                onLineItemFreeAdded={() => null}
                onTotalsChanged={publish}
                onDelete={() => undefined}
              />
              {groups[index].map(lineIndex => {
                const line = review.lineItems[lineIndex];
                const rates =
                  context?.taxRates.filter(
                    rate => (!rate.validFrom || rate.validFrom <= line.date) && (!rate.validTo || rate.validTo >= line.date),
                  ) || [];
                const selected = rates.find(rate => rate.id === line.taxRateId);
                if (line.taxShares.length > 1 && (!pdfImportHasMixedTax(line) || editingTaxes.includes(lineIndex))) return splitForm(lineIndex);
                if (pdfImportHasMixedTax(line) && !editingTaxes.includes(lineIndex))
                  return (
                    <Stack key={lineIndex} direction="row" alignItems="center" spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        {tr('pdf-import-mixed-summary', {
                          item: line.description,
                          shares: line.taxShares.map(share => `${percent(share.rate!)} % (${Math.round(share.share! * 100)} %)`).join(' · '),
                        })}
                      </Typography>
                      <Button size="small" disabled={disabled} onClick={() => setEditingTaxes(current => [...current, lineIndex])}>
                        {tr('pdf-import-change')}
                      </Button>
                    </Stack>
                  );
                if (selected && !editingTaxes.includes(lineIndex))
                  return (
                    <Stack key={lineIndex} direction="row" alignItems="center" spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        {tr('pdf-import-tax-summary', { item: line.description, rate: (selected.rate * 100).toFixed(2) })}
                      </Typography>
                      <Button size="small" disabled={disabled} onClick={() => setEditingTaxes(current => [...current, lineIndex])}>
                        {tr('pdf-import-change')}
                      </Button>
                    </Stack>
                  );
                return (
                  <TextField
                    key={lineIndex}
                    select
                    size="small"
                    sx={{ width: 280, maxWidth: '100%', alignSelf: 'flex-start' }}
                    disabled={disabled || !context}
                    value={line.taxRateId || ''}
                    label={tr('pdf-import-tax-for', { item: line.description || tr('pdf-import-item', { n: lineIndex + 1 }) })}
                    error={!line.taxRateId}
                    helperText={!line.taxRateId ? tr('pdf-import-tax-choose-hint') : tr('pdf-import-tax-change-hint')}
                    onChange={event => {
                      const value = String(event.target.value);
                      if (value.startsWith('mixed:')) onMixedChange(lineIndex, value.slice(6).split(',').map(Number));
                      else onTaxChange(lineIndex, Number(value) || null);
                      setEditingTaxes(current => current.filter(index => index !== lineIndex));
                    }}
                  >
                    <MenuItem value="">{tr('pdf-import-tax-choose')}</MenuItem>
                    {rates.map(rate => (
                      <MenuItem key={rate.id} value={rate.id}>
                        {rate.name} — {(rate.rate * 100).toFixed(2)} %
                      </MenuItem>
                    ))}
                    {rates
                      .filter(rate => rate.rate > 0)
                      .flatMap((low, index, positive) => positive.slice(index + 1).map(high => [low, high].sort((a, b) => a.rate - b.rate)))
                      .map(([low, high]) => (
                        <MenuItem key={`mixed:${low.id},${high.id}`} value={`mixed:${low.id},${high.id}`}>
                          {tr('pdf-import-tax-mixed-option', { low: percent(low.rate), high: percent(high.rate) })}
                        </MenuItem>
                      ))}
                  </TextField>
                );
              })}
            </React.Fragment>
          ))}
          {!blocks.length && <Typography color="text.secondary">{tr('pdf-import-no-services')}</Typography>}
        </Stack>
      </DragDropContext>
    </FormProvider>
  );
}
